// components/views/sections/SkillsBarChart.js
import { GraduationCap } from 'lucide-react';

export const SkillsBarChart = ({ 
    categories,
    completedSkills,
    skillsByDomain,
    skillsData = {},
    customSkillDomains = []
}) => {
    // Vérifications de sécurité
    const safeCategories = categories || [];
    const safeCompletedSkills = completedSkills || [];
    const safeSkillsByDomain = skillsByDomain || {};
    const safeSkillsData = skillsData || {};
    const safeCustomSkillDomains = customSkillDomains || [];

    // Créer les domaines personnalisés au format category
    const customCategories = safeCustomSkillDomains.map((domainName, index) => ({
        id: `custom-${index}`,
        name: domainName,
        icon: '👤',
        isCustom: true
    }));

    // Combiner toutes les catégories
    const allCategories = [...safeCategories, ...customCategories];

    // Récupérer toutes les compétences avec leurs données
    const getAllSkillsWithData = () => {
        const allSkills = [];

        allCategories.forEach(category => {
            const domainSkills = safeSkillsByDomain[category.id] || [];
            domainSkills.forEach(skillName => {
                const skillData = safeSkillsData[skillName] || {};
                allSkills.push({
                    name: skillName,
                    categoryId: category.id,
                    categoryName: category.name,
                    categoryIcon: category.icon,
                    rating: skillData.rating || 0,
                    hours: skillData.hours || 0,
                    certification: skillData.certification || '',
                    trainingLink: skillData.trainingLink || '',
                    notes: skillData.notes || '',
                    isCompleted: safeCompletedSkills.some(cs => cs.name === skillName || cs.id === skillName),
                    // Score composite basé sur rating et complétion
                    score: skillData.rating ? skillData.rating * 20 : (safeCompletedSkills.some(cs => cs.name === skillName || cs.id === skillName) ? 100 : 0)
                });
            });
        });

        return allSkills;
    };

    const allSkills = getAllSkillsWithData();

    // Si aucune compétence, afficher un message informatif
    if (allSkills.length === 0) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                    <GraduationCap className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium mb-2">Aucune compétence définie</p>
                    <p className="text-sm mb-4">
                        Ajoutez des compétences aux domaines pour visualiser votre progression
                    </p>
                    <div className="text-xs text-gray-400">
                        Le graphique en barres vous permettra de comparer vos compétences facilement
                    </div>
                </div>
            </div>
        );
    }

    // Trier les compétences par score décroissant
    const sortedSkills = [...allSkills].sort((a, b) => b.score - a.score);

    // Fonction pour obtenir la couleur basée sur le score/rating (même palette que heatmap)
    const getColor = (skill) => {
        if (skill.isCompleted) {
            return '#0e9488'; // Vert teal pour compétences complétées
        }

        if (skill.rating > 0) {
            if (skill.rating >= 4) return '#0e9488'; // Vert teal - Maîtrisé (4-5)
            if (skill.rating === 3) return '#ffad65'; // Orange - Bon (3)
            return '#e57a86'; // Rouge/rose - À travailler (1-2)
        }

        return '#94a3b8'; // Gris - Non évalué
    };

    return (
        <div className="h-full flex flex-col">
            {/* Graphique en barres */}
            <div className="flex-1 flex flex-col gap-2 overflow-y-auto">
                {sortedSkills.map((skill, index) => (
                    <div key={`${skill.categoryId}-${skill.name}`} className="flex items-center gap-3 min-h-[48px] group">
                        {/* Section gauche: Icône + trait + nom */}
                        <div className="flex items-center gap-3 w-64 flex-shrink-0">
                            {/* Icône */}
                            <div className="flex items-center justify-center w-6 h-6 flex-shrink-0">
                                <GraduationCap className="w-4 h-4 text-gray-500" />
                            </div>

                            {/* Trait vertical */}
                            <div className="w-px h-6 bg-gray-300 flex-shrink-0"></div>

                            {/* Nom de la compétence */}
                            <div className="flex-1 min-w-0">
                                <div className="font-medium text-xs text-gray-900 leading-tight mb-0.5 truncate" title={skill.name}>
                                    {skill.name}
                                </div>
                                <div className="text-xs text-gray-500 flex items-center gap-2">
                                    <span>{skill.categoryName}</span>
                                    {skill.hours > 0 && (
                                        <span className="text-purple-600">• {skill.hours}h</span>
                                    )}
                                    {skill.certification && (
                                        <span className="text-yellow-600">• Cert.</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Trait vertical séparateur principal */}
                        <div className="w-px h-10 bg-gray-300 flex-shrink-0"></div>

                        {/* Section droite: Barres de progression */}
                        <div className="flex-1 flex items-center gap-3">
                            <div className="flex-1 bg-gray-200 rounded-md h-5 relative overflow-hidden">
                                <div
                                    className="h-full transition-all duration-300 ease-out rounded-md"
                                    style={{
                                        width: `${Math.max(skill.score, 5)}%`,
                                        backgroundColor: getColor(skill)
                                    }}
                                >
                                </div>
                                
                                {/* Pourcentage/Rating overlay */}
                                <div className="absolute inset-0 flex items-center justify-start pl-2">
                                    <span className={`text-xs font-semibold ${
                                        skill.score === 0 ? 'text-gray-500' : 
                                        skill.score >= 60 ? 'text-white' : 'text-gray-700'
                                    }`}>
                                        {skill.isCompleted ? '✓ Complété' : 
                                         skill.rating > 0 ? `${skill.rating}/5` : 'Non évalué'}
                                    </span>
                                </div>
                            </div>

                            {/* Score détaillé */}
                            <div className="flex items-center gap-1 text-xs text-gray-600 w-12 flex-shrink-0 font-medium">
                                <span className="text-gray-400">●</span>
                                <span>{skill.score}%</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Légende */}
            <div className="mt-4 pt-3 border-t border-gray-200 flex-shrink-0">
                <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Échelle de maîtrise :</span>
                    <div className="flex items-center gap-1">
                        <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded" style={{ backgroundColor: '#94a3b8' }}></div>
                            <span className="text-gray-500">Non évalué</span>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                            <div className="w-2 h-2 rounded" style={{ backgroundColor: '#e57a86' }}></div>
                            <span className="text-gray-500">1-2/5</span>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                            <div className="w-2 h-2 rounded" style={{ backgroundColor: '#ffad65' }}></div>
                            <span className="text-gray-500">3/5</span>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                            <div className="w-2 h-2 rounded" style={{ backgroundColor: '#0e9488' }}></div>
                            <span className="text-gray-500">4-5/5</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};